import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useTenantContext } from '../../context/TenantContext';
import { ArrowLeft, Trash2, ShoppingBag, CreditCard, Loader2, Minus, Plus } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface CartItem {
  productId: number;
  title: string;
  price: number;
  currency: string;
  quantity: number;
  imageUrl?: string;
  slug: string;
}

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'ShoppingBag', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Charger le panier depuis localStorage
    const loadCart = () => {
      setIsLoading(true);
      try {
        const cartString = localStorage.getItem('shop_cart');
        const cart = cartString ? JSON.parse(cartString) : [];
        setCartItems(cart);
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger votre panier',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadCart();
  }, [toast]);

  // Calculer le total du panier
  const cartTotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  const formattedTotal = new Intl.NumberFormat('fr-FR').format(cartTotal);
  const currency = 'XAF'; // XAF est le code ISO pour le franc CFA

  // Mettre à jour la quantité d'un article
  const updateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cartItems.map(item => 
      item.productId === productId ? { ...item, quantity: newQuantity } : item
    );
    
    setCartItems(updatedCart);
    localStorage.setItem('shop_cart', JSON.stringify(updatedCart));
  };

  // Supprimer un article du panier
  const removeItem = (productId: number) => {
    const updatedCart = cartItems.filter(item => item.productId !== productId);
    setCartItems(updatedCart);
    localStorage.setItem('shop_cart', JSON.stringify(updatedCart));
        toast({
        title: 'Produit retiré',
        description: 'Le produit a été retiré de votre panier',
        variant: 'default'
      });
  };

  // Vider le panier
  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('shop_cart');
        toast({
        title: 'Panier vidé',
        description: 'Tous les produits ont été retirés de votre panier',
        variant: 'default'
      });
  };

  // Passer à la page de paiement
  const proceedToCheckout = () => {
    // Vérifier si le panier est vide
    if (cartItems.length === 0) {
      toast({
        title: 'Panier vide',
        description: 'Votre panier est vide. Ajoutez des produits avant de finaliser votre commande.',
        variant: 'destructive'
      });
      return;
    }
    
    navigate('/boutique/checkout');
  };

    if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen text-white" style={{ backgroundColor: tenant?.config?.color2 || '#08062b' }}>
          <div className="flex flex-col justify-center items-center h-screen">
            <Loader2 className="animate-spin h-12 w-12" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
            <span className="mt-6 text-xl" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Chargement du panier...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Votre Panier - SIS"
        description="Consultez les produits dans votre panier et finalisez votre commande."
      />

      <div className="text-white relative overflow-hidden pt-20">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.ShoppingBag;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        {/* Section Hero */}
        <div className="relative isolate overflow-hidden pt-8 sm:pt-12 pb-16">
          <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-6 py-3 mb-8"
                style={{ 
                  backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                  borderWidth: '1px'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <ShoppingBag className="w-5 h-5" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                <span className="font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Panier d'Achat</span>
              </motion.div>
              
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-white sm:text-7xl mb-6">
                <span className="relative inline-block">
                  <span 
                    className="absolute -inset-x-6 -inset-y-2 -z-10 rounded-full blur-lg"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.3)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)}, transparent)`
                    }}
                  ></span>
                  Votre Panier
                </span>
              </h1>
              <p className="text-xl text-white/80 max-w-2xl mx-auto">Vérifiez vos produits avant de finaliser la commande.</p>
            </motion.div>
          </div>
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-20">

          {cartItems.length > 0 ? (
            <motion.div 
              className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
            >
              {/* Liste des articles */}
              <div className="lg:col-span-2">
                <Card className="bg-black/20 border-white/10 text-white backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle>Articles ({cartItems.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-white/10">
                      {cartItems.map((item) => (
                        <div key={item.productId} className="flex items-center space-x-4 p-4">
                          <img 
                            src={item.imageUrl || 'https://via.placeholder.com/100'}
                            alt={item.title}
                            className="w-24 h-24 rounded-md object-cover border border-white/10"
                          />
                          <div className="flex-1 min-w-0">
                            <Link to={`/boutique/${item.slug}`} className="font-semibold text-white hover:text-white transition-colors duration-200"
                              style={{ color: tenant?.config?.color3 || '#ffffff' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                              }}
                            >
                              {item.title}
                            </Link>
                            <p className="text-sm text-white/70 mt-1">
                              {new Intl.NumberFormat('fr-FR').format(item.price) + ' FCFA'}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center border border-white/10 rounded-md bg-white/5">
                              <Button variant="ghost" size="icon" onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="h-8 w-8">
                                <Minus size={16}/>
                              </Button>
                              <span className="w-10 text-center font-semibold">{item.quantity}</span>
                              <Button variant="ghost" size="icon" onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="h-8 w-8">
                                <Plus size={16}/>
                              </Button>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeItem(item.productId)} className="text-white/50 h-8 w-8 transition-colors duration-200"
                              style={{ 
                                color: tenant?.config?.color4 || '#9ca3af',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = tenant?.config?.color4 || '#9ca3af';
                              }}
                            >
                              <Trash2 size={18} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter className="p-4 flex justify-between"
                    style={{
                      backgroundColor: `${tenant?.config?.color2 || '#08062b'}33`
                    }}
                  >
                    <Button variant="outline" onClick={clearCart} className="transition-all duration-200"
                      style={{
                        borderColor: `${tenant?.config?.color1 || '#D4AF37'}80`,
                        color: tenant?.config?.color1 || '#D4AF37'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#D4AF37'}1a`;
                        e.currentTarget.style.color = tenant?.config?.color1 || '#D4AF37';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = tenant?.config?.color1 || '#D4AF37';
                      }}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Vider le panier
                    </Button>
                    <Button asChild variant="ghost" className="text-white/70 hover:text-white transition-colors duration-200">
                      <Link to="/boutique">
                        <ShoppingBag size={16} className="mr-2" />
                        Continuer les achats
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
              
              {/* Résumé de la commande */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <Card className="bg-white/10 border text-white backdrop-blur-sm shadow-xl"
                    style={{
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                    }}
                  >
                    <CardHeader>
                      <CardTitle>Résumé</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between text-white/80">
                        <span>Sous-total</span>
                        <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cartTotal)}</span>
                      </div>
                      <div className="flex justify-between text-white/80">
                        <span>Livraison</span>
                        <span>À calculer</span>
                      </div>
                      <div className="border-t border-white/10 pt-4 flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cartTotal)}</span>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button onClick={proceedToCheckout} size="lg" className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#08062b',
                          color: tenant?.config?.color3 || '#ffffff',
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                          boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#08062b', 0.4)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
                          e.currentTarget.style.boxShadow = `0 6px 20px ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.6)}`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = tenant?.config?.color2 || '#08062b';
                          e.currentTarget.style.boxShadow = `0 4px 15px ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.4)}`;
                        }}
                      >
                        <CreditCard size={20} className="mr-2" />
                        Finaliser la commande
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              className="text-center py-20 backdrop-blur-sm rounded-xl shadow-xl"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`,
                borderWidth: '1px'
              }}
              variants={fadeIn}
              initial="hidden"
              animate="visible"
            >
              <div className="relative">
                <ShoppingBag size={48} className="mx-auto mb-6" style={{ color: `${tenant?.config?.color1 || '#fe8d17'}70` }} />
                <h3 className="text-2xl font-bold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Votre panier est vide</h3>
                <p className="mb-8 max-w-md mx-auto" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                  Vous n'avez pas encore ajouté de produits à votre panier. Commencez à explorer notre boutique pour trouver ce dont vous avez besoin.
                </p>
                <Button asChild size="lg" className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  style={{
                    backgroundColor: tenant?.config?.color2 || '#08062b',
                    color: tenant?.config?.color3 || '#ffffff',
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                    boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.4)}`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
                    e.currentTarget.style.boxShadow = `0 6px 20px ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.6)}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = tenant?.config?.color2 || '#08062b';
                    e.currentTarget.style.boxShadow = `0 4px 15px ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.4)}`;
                  }}
                >
                  <Link to="/boutique">
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    Explorer la boutique
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default CartPage;
